import { m } from "./schema";

// No-knead sourdough — the canonical overnight bake. The 10-hour
// bulk cold ferment is the Day 1 / Day 2 break. Day 1 is mix +
// autolyse + folds + into the fridge before bed. Day 2 is shape +
// proof + bake.
const recipe = {
  slug: "no-knead-sourdough",
  title: "No-Knead Sourdough",
  subtitle: "Active starter, 10-hour cold bulk, dutch-oven bake",
  emoji: "🍞",

  cuisine: "american",
  category: "bread",
  difficulty: 5,
  routes: ["plan", "learn"],
  time: { prep: 25, cook: 50 },
  serves: 8,

  skills: [
    { id: "timing", weight: 0.5, xp: 80 },
    { id: "heat",   weight: 0.3, xp: 50 },
    { id: "knife",  weight: 0.2, xp: 25 },
  ],
  minSkillLevels: { timing: 2 },

  tools: ["Dutch oven (4–6 qt)", "Kitchen scale", "Bench scraper", "Banneton or 8\" bowl + tea towel", "Lame or sharp paring knife"],
  ingredients: [
    { amount: "100 g",  item: "active sourdough starter (peak, doubled)" },
    { amount: "375 g",  item: "warm water (~85°F)" },
    { amount: "500 g",  item: "bread flour",         ingredientId: "bread_flour",        qty: { amount: 500, unit: "g" } },
    { amount: "10 g",   item: "fine sea salt" },
    { amount: "1 tbsp", item: "rice flour or semolina (for dusting the banneton)" },
  ],

  prepSteps: [
    {
      id: "feed-starter",
      leadMinutes: 12 * 60,
      emoji: "🧊",
      title: "Feed the starter",
      body: "Pull starter from the fridge and feed it 1:1:1 (50g starter, 50g water, 50g flour) the night before so it's at peak when you mix tomorrow night. Look for doubled volume + domed top.",
      defaultOn: true,
      source: "freeze_overnight",
    },
  ],

  steps: [
    { id:1, title:"Mix the levain", instruction:"In your mixing bowl, whisk 100g active starter into 375g warm water until milky and lump-free. The starter should float — if it sinks, give it another 1–2 hours to peak.", icon:"🌊", animation:"stir", timer:m(5), tip:"Float test = enough trapped CO₂ to leaven the loaf. Sinking starter makes a denser bread.",
      uses:[
        { amount:"100 g", item:"active sourdough starter" },
        { amount:"375 g", item:"warm water (~85°F)" },
      ],
      heat:"off", doneCue:"starter dispersed, water cloudy" },
    { id:2, title:"Autolyse", instruction:"Add 500g bread flour. Mix with a wet hand until no dry pockets remain — shaggy, not smooth. Cover. Rest 30 min so the flour fully hydrates and gluten begins to organize on its own.", icon:"🌾", animation:"stir", timer:m(30), tip:"Autolyse does the kneading for you. Skip it and you'll need to knead 10x longer to get the same dough strength.",
      uses:[{ amount:"500 g", item:"bread flour", ingredientId:"bread_flour" }],
      heat:"off", doneCue:"shaggy mass, no dry flour, surface rough" },
    { id:3, title:"Add the salt", instruction:"Sprinkle 10g salt and a splash of water (~15g) on top. Pinch and squeeze the salt through the dough until fully incorporated, about 3–4 minutes. The dough will tighten then loosen.", icon:"🧂", animation:"stir", timer:m(5), tip:"Salt added AFTER autolyse. Adding it earlier slows down the gluten and the enzymes; adding it now lets the autolyse do its thing first.",
      uses:[{ amount:"10 g", item:"fine sea salt" }],
      heat:"off", doneCue:"salt fully dissolved, dough smooth and tacky" },
    { id:4, title:"Stretch & folds (×3, 30 min apart)", instruction:"For the next 90 minutes, perform 3 sets of stretch-and-folds — wet hand, lift one quadrant of the dough up and over the center, rotate the bowl 90° and repeat 3 more times. Cover between sets. The dough will go from shaggy to smooth and pillowy.", icon:"🤲", animation:"stir", timer:m(90), tip:"This builds the gluten without kneading. By the third set the dough should hold its shape and feel airy.",
      uses:[],
      heat:"off", doneCue:"dough smooth, jiggly, holds a fold without tearing" },
    { id:5, title:"Cold bulk ferment overnight", instruction:"Cover the bowl with plastic or a lid. Move it to the fridge for 10–12 hours — the cold slows the yeast so flavor deepens without overproofing. You'll come back to it in the morning.", icon:"🌙", animation:null, timer:m(10*60), overnight:true, tip:"You can stretch this to 36 hours for a more sour, more complex loaf. Don't go below 8 hours — the dough won't have developed enough flavor.",
      uses:[],
      heat:"off", doneCue:"dough domed, jiggly, ~50% larger than at start" },
    { id:6, title:"Shape & bench rest", instruction:"Tip the dough onto a lightly floured counter. Pre-shape into a loose round, rest 10 min, then shape tightly — fold edges into the center, flip seam-down, drag in a circle to build tension. Place seam-up in a banneton dusted with rice flour. Rest 20 min while the oven heats.", icon:"🥖", animation:"toss", timer:m(30), tip:"The pre-shape sets the structure; the final shape sets the surface tension. Both matter for oven spring.",
      uses:[{ amount:"1 tbsp", item:"rice flour for dusting" }],
      heat:"off", doneCue:"loaf taut, holds its shape, seam well-pinched" },
    { id:7, title:"Preheat the dutch oven", instruction:"Crank the oven to 500°F with the dutch oven (lid on) inside. Preheat for 45 minutes. The dutch oven needs to be ripping hot to give you the steam burst that creates oven spring and a crackly crust.", icon:"🔥", animation:"bloom", timer:m(45), tip:"500°F is not optional. Anything less and you trade oven spring for a dense, pale crumb.",
      uses:[],
      heat:"high", doneCue:"dutch oven smoking-hot, oven fully calibrated to 500°F" },
    { id:8, title:"Score & bake covered", instruction:"Pull the dutch oven out. Tip the loaf onto parchment, score a confident 1/2-inch-deep slash across the top with the lame. Lower the loaf (parchment and all) into the dutch oven, lid on, drop heat to 475°F. Bake covered 20 minutes.", icon:"🫕", animation:"bloom", timer:m(20), tip:"One decisive cut is better than three timid ones. The score guides where the loaf bursts open as it springs.",
      uses:[],
      heat:"high", doneCue:"loaf has lifted, score has opened into 'ears'" },
    { id:9, title:"Bake uncovered", instruction:"Remove the lid. Drop heat to 450°F and bake another 20–25 minutes until the crust is deep mahogany and the internal temp reads 205–210°F. Let cool on a rack at least 1 hour before slicing — cutting too early gums the crumb.", icon:"✨", animation:"plate", timer:m(25), tip:"Patience on the cool. The crumb finishes setting as steam escapes; slicing at 30 min gives you a wet, gummy interior even though the loaf 'looks' done.",
      uses:[],
      heat:"high", doneCue:"deep mahogany crust, hollow when tapped, internal 205°F+" },
  ],

  tags: ["bread", "overnight", "weekend project", "sourdough"],

  reheat: {
    primary: {
      method: "oven",
      tempF: 350,
      timeMin: 8,
      covered: false,
      tips: "Slice and toast directly, or warm a whole quarter-loaf 8 min at 350°F to revive the crust. Microwave kills the crumb — never.",
    },
    note: "Best the day-of. Day 2 onward, slice and freeze; toast straight from frozen.",
  },
};

export default recipe;
